import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Tabs,
  Tab,
  Table,
  Alert,
  Form,
  ButtonGroup,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart,
  ReferenceLine,
  Scatter,
  ScatterChart,
  ZAxis,
} from "recharts";
import api from "../api/api";
import moment from "moment";
import "../assets/styling/Styles.css";

const CalculationDetails = () => {
  const { cropId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [weeksData, setWeeksData] = useState([]);
  const [seasonData, setSeasonData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [chartResolution, setChartResolution] = useState("daily"); // daily, weekly, all

  // Safe number conversion
  const safeNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return decimals > 0 ? "0.00" : 0;
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return decimals > 0 ? "0.00" : 0;
    return decimals > 0 ? num.toFixed(decimals) : num;
  };

  useEffect(() => {
    fetchCalculations();
  }, [cropId]);

  const fetchCalculations = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/calcul/season-calcul/${cropId}`);
      if (response.data.type === "success") {
        const weeks = response.data.weeks || [];
        setWeeksData(weeks);
        setSeasonData(response.data.crop);
        if (weeks.length > 0 && weeks[0].daily_calculations?.length > 0) {
          setSelectedWeek(weeks[0]);
          setSelectedDay(weeks[0].daily_calculations[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching calculations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get all daily calculations across all weeks
  const getAllDailyData = useMemo(() => {
    const allDays = [];
    weeksData.forEach((week, weekIdx) => {
      const dailyCalcs = week.daily_calculations || [];
      dailyCalcs.forEach((day) => {
        allDays.push({
          ...day,
          weekNumber: weekIdx + 1,
          dateFormatted: moment(day.date).format("MMM DD"),
          fullDate: day.date,
          bilanValue:
            day.bilan && Array.isArray(day.bilan)
              ? day.bilan[day.bilan.length - 1]?.value || 0
              : 0,
        });
      });
    });
    return allDays.sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  }, [weeksData]);

  // Sample data for performance
  const sampleData = (data, maxPoints = 80) => {
    if (data.length <= maxPoints) return data;
    const step = Math.ceil(data.length / maxPoints);
    return data.filter((_, index) => index % step === 0);
  };

  // Get optimized daily data based on resolution
  const getOptimizedDailyData = useMemo(() => {
    const allDays = getAllDailyData;
    if (chartResolution === "all") {
      return sampleData(allDays, 80);
    }
    return allDays;
  }, [getAllDailyData, chartResolution]);

  // Get weekly summary data
  const getWeeklySummary = useMemo(() => {
    return weeksData.map((week, idx) => {
      const dailyCalcs = week.daily_calculations || [];
      const totalRain = dailyCalcs.reduce(
        (sum, day) => sum + (day.rain || 0),
        0,
      );
      const totalIrrigation = dailyCalcs.reduce(
        (sum, day) => sum + (day.irrigation || 0),
        0,
      );
      const avgEtc =
        dailyCalcs.length > 0
          ? dailyCalcs.reduce((sum, day) => sum + (day.Etc || 0), 0) /
            dailyCalcs.length
          : 0;
      const avgET0 =
        dailyCalcs.length > 0
          ? dailyCalcs.reduce((sum, day) => sum + (day.ET0 || 0), 0) /
            dailyCalcs.length
          : 0;
      const avgKc =
        dailyCalcs.length > 0
          ? dailyCalcs.reduce((sum, day) => sum + (day.kc || 0), 0) /
            dailyCalcs.length
          : 0;
      const finalBilan =
        dailyCalcs.length > 0
          ? dailyCalcs[dailyCalcs.length - 1].bilan?.[23]?.value || 0
          : 0;
      const avgBilan =
        dailyCalcs.length > 0
          ? dailyCalcs.reduce((sum, day) => {
              const bilan =
                day.bilan && Array.isArray(day.bilan)
                  ? day.bilan[day.bilan.length - 1]?.value || 0
                  : 0;
              return sum + bilan;
            }, 0) / dailyCalcs.length
          : 0;
      const stressDays = dailyCalcs.filter((d) => {
        const bilan =
          d.bilan && Array.isArray(d.bilan)
            ? d.bilan[d.bilan.length - 1]?.value || 0
            : 0;
        return bilan < 75;
      }).length;

      return {
        weekNumber: idx + 1,
        weekLabel: ` ${idx + 1}`,
        dateRange: `${moment(week.week_start).format("MMM DD")} - ${moment(
          week.week_end,
        ).format("MMM DD")}`,
        totalRain: parseFloat(totalRain.toFixed(2)),
        totalIrrigation: parseFloat(totalIrrigation.toFixed(2)),
        avgEtc: parseFloat(avgEtc.toFixed(2)),
        avgET0: parseFloat(avgET0.toFixed(2)),
        avgKc: parseFloat(avgKc.toFixed(3)),
        finalBilan: parseFloat(finalBilan.toFixed(2)),
        avgBilan: parseFloat(avgBilan.toFixed(2)),
        irrigationDays: dailyCalcs.filter((d) => d.irrigation > 0).length,
        refusedDays: dailyCalcs.filter((d) => d.refused).length,
        stressDays,
        waterBalance: parseFloat(
          (totalRain + totalIrrigation - avgEtc * dailyCalcs.length).toFixed(2),
        ),
      };
    });
  }, [weeksData]);

  // Get hourly data for selected day
  const getHourlyData = (day) => {
    if (!day || !day.bilan || !Array.isArray(day.bilan)) return [];

    return day.bilan.map((hourData) => ({
      hour: `${String(hourData.hour).padStart(2, "0")}:00`,
      hourNumber: hourData.hour,
      bilan: parseFloat((hourData.value || 0).toFixed(2)),
      etc:
        hourData.hour >= 6 && hourData.hour <= 18
          ? parseFloat((day.Etc / 13).toFixed(3))
          : parseFloat((day.Etc / 26).toFixed(3)),
      periodKey:
        hourData.hour >= 6 && hourData.hour < 12
          ? "period_morning"
          : hourData.hour >= 12 && hourData.hour < 18
            ? "period_afternoon"
            : hourData.hour >= 18 && hourData.hour < 22
              ? "period_evening"
              : "period_night",
    }));
  };

  // Get RU min/max from the first available day's data
  const { ruMax, ruMin } = useMemo(() => {
    const firstDay = weeksData[0]?.daily_calculations?.[0];
    return {
      ruMax: firstDay?.ru_max || 100,
      ruMin: firstDay?.ru_min || 0,
    };
  }, [weeksData]);
  const getSeasonStats = useMemo(() => {
    const allDays = getAllDailyData;
    if (allDays.length === 0) return null;

    const totalRain = allDays.reduce((sum, day) => sum + (day.rain || 0), 0);
    const totalIrrigation = allDays.reduce(
      (sum, day) => sum + (day.irrigation || 0),
      0,
    );
    const totalEtc = allDays.reduce((sum, day) => sum + (day.Etc || 0), 0);
    const avgET0 =
      allDays.reduce((sum, day) => sum + (day.ET0 || 0), 0) / allDays.length;
    const avgEtc = totalEtc / allDays.length;
    const maxEtc = Math.max(...allDays.map((d) => d.Etc || 0));
    const minEtc = Math.min(...allDays.map((d) => d.Etc || 0));
    const irrigationDays = allDays.filter((d) => d.irrigation > 0).length;
    const rainyDays = allDays.filter((d) => d.rain > 0).length;
    const finalBilan = allDays[allDays.length - 1]?.bilanValue || 0;
    const avgBilan =
      allDays.reduce((sum, day) => sum + (day.bilanValue || 0), 0) /
      allDays.length;
    const minBilan = Math.min(...allDays.map((d) => d.bilanValue || 0));
    const maxBilan = Math.max(...allDays.map((d) => d.bilanValue || 0));
    const stressDays = allDays.filter((d) => d.bilanValue < 75).length;

    const totalWaterSupplied = totalRain + totalIrrigation;
    const irrigationEfficiency =
      totalEtc > 0 ? (totalIrrigation / totalEtc) * 100 : 0;
    const rainContribution =
      totalWaterSupplied > 0 ? (totalRain / totalWaterSupplied) * 100 : 0;
    const avgIrrigationDepth =
      irrigationDays > 0 ? totalIrrigation / irrigationDays : 0;

    return {
      totalRain: parseFloat(totalRain.toFixed(2)),
      totalIrrigation: parseFloat(totalIrrigation.toFixed(2)),
      totalEtc: parseFloat(totalEtc.toFixed(2)),
      avgET0: parseFloat(avgET0.toFixed(2)),
      avgEtc: parseFloat(avgEtc.toFixed(2)),
      maxEtc: parseFloat(maxEtc.toFixed(2)),
      minEtc: parseFloat(minEtc.toFixed(2)),
      irrigationDays,
      rainyDays,
      totalDays: allDays.length,
      finalBilan: parseFloat(finalBilan.toFixed(2)),
      avgBilan: parseFloat(avgBilan.toFixed(2)),
      minBilan: parseFloat(minBilan.toFixed(2)),
      maxBilan: parseFloat(maxBilan.toFixed(2)),
      stressDays,
      waterDeficit: parseFloat(
        (totalEtc - totalRain - totalIrrigation).toFixed(2),
      ),
      totalWaterSupplied: parseFloat(totalWaterSupplied.toFixed(2)),
      irrigationEfficiency: parseFloat(irrigationEfficiency.toFixed(1)),
      rainContribution: parseFloat(rainContribution.toFixed(1)),
      avgIrrigationDepth: parseFloat(avgIrrigationDepth.toFixed(2)),
      stressPercentage: parseFloat(
        ((stressDays / allDays.length) * 100).toFixed(1),
      ),
    };
  }, [getAllDailyData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            padding: "12px 16px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <p style={{ fontWeight: "600", marginBottom: "8px", color: "#333" }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p
              key={index}
              style={{ color: entry.color, margin: "4px 0", fontSize: "13px" }}
            >
              <span style={{ fontWeight: "500" }}>{entry.name}:</span>{" "}
              {safeNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Container fluid className="main-content-container px-3 py-5">
        <div style={{ textAlign: "center", padding: "100px 0" }}>
          <div
            className="spinner-border text-info"
            role="status"
            style={{ width: "4rem", height: "4rem" }}
          >
            <span className="visually-hidden">{t("loading")}</span>
          </div>
          <p className="mt-3 text-muted">{t("loading_calculations")}</p>
        </div>
      </Container>
    );
  }

  const stats = getSeasonStats;
  const dailyData = getOptimizedDailyData;
  const weeklySummary = getWeeklySummary;
  const hourlyData = selectedDay ? getHourlyData(selectedDay) : [];

  return (
    <Container fluid className="main-content-container px-3 py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <Button
                variant="outline-secondary"
                onClick={() => navigate(-1)}
                style={{
                  borderRadius: "50%",
                  width: "45px",
                  height: "45px",
                  padding: 0,
                  border: "2px solid #dee2e6",
                }}
              >
                <i className="fas fa-arrow-left" aria-hidden="true"></i>
              </Button>
              <div>
                <h2
                  className="mb-1"
                  style={{ fontWeight: "700", color: "#26a6b7" }}
                >
                  {t("seasonal_title")}
                </h2>
                <p className="mb-0 text-muted">
                  {seasonData?.name || t("season")} •{" "}
                  {moment(seasonData?.start_date).format("MMM DD")} -{" "}
                  {moment(seasonData?.end_date).format("MMM DD, YYYY")}
                </p>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Enhanced Statistics Cards */}
      {stats && (
        <>
          <Row className="mb-4 g-3 d-flex justify-content-around">
            <Col md={6} lg={3}>
              <Card
                className="text-center border-0 shadow-sm h-100"
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <Card.Body>
                  <div className="mb-2">
                    <i className="fas fa-seedling fa-2x opacity-75" aria-hidden="true"></i>
                  </div>
                  <h6 className="mb-1 opacity-75">{t("avg_etc")}</h6>
                  <h3 className="mb-0" style={{ fontWeight: "700" }}>
                    {stats.avgEtc}{" "}
                    <small style={{ fontSize: "0.5em" }}>mm/day</small>
                  </h3>
                  <small className="opacity-75">
                    {t("range")}: {stats.minEtc} - {stats.maxEtc} mm
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card
                className="text-center border-0 shadow-sm h-100"
                style={{
                  background:
                    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  color: "white",
                }}
              >
                <Card.Body>
                  <div className="mb-2">
                    <i className="fas fa-tint fa-2x opacity-75" aria-hidden="true"></i>
                  </div>
                  <h6 className="mb-1 opacity-75">{t("total_irrigation")}</h6>
                  <h3 className="mb-0" style={{ fontWeight: "700" }}>
                    {stats.totalIrrigation}{" "}
                    <small style={{ fontSize: "0.5em" }}>mm</small>
                  </h3>
                  <small className="opacity-75">
                    {stats.irrigationDays} {t("applications_avg")} • Avg:{" "}
                    {stats.avgIrrigationDepth} mm
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card
                className="text-center border-0 shadow-sm h-100"
                style={{
                  background:
                    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                  color: "white",
                }}
              >
                <Card.Body>
                  <div className="mb-2">
                    <i className="fas fa-cloud-rain fa-2x opacity-75" aria-hidden="true"></i>
                  </div>
                  <h6 className="mb-1 opacity-75">{t("total_rain")}</h6>
                  <h3 className="mb-0" style={{ fontWeight: "700" }}>
                    {stats.totalRain}{" "}
                    <small style={{ fontSize: "0.5em" }}>mm</small>
                  </h3>
                  <small className="opacity-75">
                    {stats.rainyDays} {t("rainy_days_water")} •{" "}
                    {stats.rainContribution}% of water
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Water Balance Summary Alert */}
      {stats && (
        <Alert
          variant={stats.waterDeficit > 10 ? "warning" : "success"}
          className="mb-4"
        >
          <div className="d-flex align-items-center">
            <i
              className={`fas ${
                stats.waterDeficit > 10
                  ? "fa-exclamation-triangle"
                  : "fa-check-circle"
              } fa-2x me-3`} aria-hidden="true"></i>
            <div>
              <strong>{t("season_summary")}</strong>
              <p className="mb-0 mt-1">
                • {t("water_supplied")}:{" "}
                <strong>{stats.totalWaterSupplied} mm</strong>(
                {stats.rainContribution}% {t("Rain")},{" "}
                {(100 - stats.rainContribution).toFixed(1)}% {t("Irrigation")})
              </p>
            </div>
          </div>
        </Alert>
      )}

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4" fill>
        {/* Daily Overview Tab */}
        <Tab
          eventKey="overview"
          title={
            <>
              <i className="fas fa-chart-line me-2" aria-hidden="true"></i>
              {t("daily_trends")}
            </>
          }
        >
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm mb-4">
                <Card.Header
                  className="bg-white"
                  style={{
                    fontWeight: "600",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <i className="fas fa-chart-area me-2 text-primary" aria-hidden="true"></i>
                      {t("daily_water_balance_components")}
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={450}>
                    <ComposedChart
                      data={dailyData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="dateFormatted"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        yAxisId="left"
                        label={{
                          value: "mm",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 1.2]}
                        label={{
                          value: "Kc",
                          angle: 90,
                          position: "insideRight",
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: "20px" }} />
                      <ReferenceLine
                        yAxisId="left"
                        y={ruMin}
                        stroke="#ff6b6b"
                        strokeDasharray="5 5"
                        label="Critical (RU min)"
                      />
                      <ReferenceLine
                        yAxisId="left"
                        y={ruMax}
                        stroke="#4caf50"
                        strokeDasharray="5 5"
                        label="Full (RU max)"
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="bilanValue"
                        name={t("water_balance")}
                        stroke="#8884d8"
                        strokeWidth={3}
                        dot={false}
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="Etc"
                        name={t("crop_et_etc")}
                        fill="#82ca9d"
                        stroke="#82ca9d"
                        fillOpacity={0.3}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="kc"
                        name={t("crop_coefficient_kc")}
                        stroke="#ff7300"
                        strokeWidth={2}
                        dot={false}
                        strokeDasharray="5 5"
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="irrigation"
                        name={t("irrigation")}
                        fill="#0088fe"
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="rain"
                        name={t("rainfall")}
                        fill="#00C49F"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <Card className="shadow-sm mb-4">
                <Card.Header
                  className="bg-white"
                  style={{
                    fontWeight: "600",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <i className="fas fa-thermometer-half me-2 text-danger" aria-hidden="true"></i>
                  {t("eto_vs_etc_comparison")}
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={dailyData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="dateFormatted"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="ET0"
                        name={t("reference_et_eto")}
                        stroke="#8884d8"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="Etc"
                        name={t("crop_et_etc")}
                        stroke="#82ca9d"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6}>
              <Card className="shadow-sm mb-4">
                <Card.Header
                  className="bg-white"
                  style={{
                    fontWeight: "600",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <i className="fas fa-chart-bar me-2 text-info" aria-hidden="true"></i>
                  {t("cumulative_water_balance")}
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart
                      data={dailyData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="dateFormatted"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <ReferenceLine
                        y={0}
                        stroke="#666"
                        strokeDasharray="3 3"
                      />
                      <ReferenceLine
                        y={75}
                        stroke="#ff6b6b"
                        strokeDasharray="3 3"
                        label="Stress"
                      />
                      <Area
                        type="monotone"
                        dataKey="bilanValue"
                        name={t("water_balance")}
                        fill="#8884d8"
                        stroke="#8884d8"
                        fillOpacity={0.6}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* Weekly Analysis Tab */}
        <Tab
          eventKey="weekly"
          title={
            <>
              <i className="fas fa-calendar-week me-2" aria-hidden="true"></i>
              {t("weekly_summary")}
            </>
          }
        >
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm mb-4">
                <Card.Header
                  className="bg-white"
                  style={{
                    fontWeight: "600",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <i className="fas fa-chart-bar me-2 text-success" aria-hidden="true"></i>
                  {t("weekly_water_budget")}
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={weeklySummary}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="weekLabel"
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis
                        label={{
                          value: "mm",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: "10px" }} />
                      <Bar
                        dataKey="avgEtc"
                        name={t("avg_etc")}
                        fill="#82ca9d"
                      />
                      <Bar
                        dataKey="totalIrrigation"
                        name={t("total_irrigation")}
                        fill="#0088fe"
                      />
                      <Bar
                        dataKey="totalRain"
                        name={t("total_rain")}
                        fill="#00C49F"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <Card className="shadow-sm mb-4">
                <Card.Header
                  className="bg-white"
                  style={{
                    fontWeight: "600",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <i className="fas fa-layer-group me-2 text-warning" aria-hidden="true"></i>
                  {t("weekly_water_budget")}
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={weeklySummary}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="weekLabel" />
                      <YAxis domain={[0, 1.2]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="avgKc"
                        name="Avg Kc"
                        stroke="#ff7300"
                        strokeWidth={3}
                        dot={{ fill: "#ff7300", r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6}>
              <Card className="shadow-sm mb-4">
                <Card.Header
                  className="bg-white"
                  style={{
                    fontWeight: "600",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <i className="fas fa-water me-2 text-primary" aria-hidden="true"></i>
                  {t("weekly_water_balance_trend")}
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart
                      data={weeklySummary}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="weekLabel" />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <ReferenceLine
                        y={0}
                        stroke="#666"
                        strokeDasharray="3 3"
                      />
                      <ReferenceLine
                        y={75}
                        stroke="#ff6b6b"
                        strokeDasharray="3 3"
                        label="Stress"
                      />
                      <Area
                        type="monotone"
                        dataKey="avgBilan"
                        name="Avg Balance"
                        fill="#8884d8"
                        stroke="#8884d8"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Weekly Data Table */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm">
                <Card.Header
                  className="bg-white"
                  style={{
                    fontWeight: "600",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <i className="fas fa-table me-2 text-secondary" aria-hidden="true"></i>
                  {t("weekly_summary_table")}
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">{t("week")}</th>
                          <th scope="col">{t("period")}</th>
                          <th scope="col" className="text-center">{t("avg_kc")}</th>
                          <th scope="col" className="text-center">{t("avg_etc_mm")}</th>
                          <th scope="col" className="text-center">{t("rain_mm")}</th>
                          <th scope="col" className="text-center">{t("irrigation_mm")}</th>
                          <th scope="col" className="text-center">{t("avg_balance_mm")}</th>
                          <th scope="col" className="text-center">
                            {t("final_balance_mm")}
                          </th>
                          <th scope="col" className="text-center">{t("actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklySummary.map((week, idx) => (
                          <tr key={idx}>
                            <td>
                              <strong>{t("week")} {week.weekLabel}</strong>
                            </td>
                            <td>
                              <small className="text-muted">
                                {week.dateRange}
                              </small>
                            </td>
                            <td className="text-center">
                              <Badge bg="warning" text="dark">
                                {week.avgKc}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="success">{week.avgEtc}</Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="info">{week.totalRain}</Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="primary">{week.totalIrrigation}</Badge>
                            </td>
                            <td className="text-center">
                              <Badge
                                bg={
                                  week.avgBilan >= ruMax
                                    ? "success"
                                    : week.avgBilan > ruMin
                                      ? "info"
                                      : "danger"
                                }
                              >
                                {week.avgBilan}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge
                                bg={
                                  week.finalBilan > 75
                                    ? "success"
                                    : week.finalBilan > 60
                                      ? "warning"
                                      : "danger"
                                }
                              >
                                {week.finalBilan}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => {
                                  setSelectedWeek(weeksData[idx]);
                                  setActiveTab("daily-detail");
                                }}
                              >
                                <i className="fas fa-calendar-day me-1" aria-hidden="true"></i>
                                {t("view_days")}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* Daily Detail Tab */}
        <Tab
          eventKey="daily-detail"
          title={
            <>
              <i className="fas fa-calendar-day me-2" aria-hidden="true"></i>
              {t("daily_detail")}
            </>
          }
        >
          {selectedWeek ? (
            <>
              <Alert variant="info" className="mb-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-info-circle fa-2x me-3" aria-hidden="true"></i>
                    <div>
                      <strong>
                        {t("viewing_week")}{" "}
                        {
                          weeklySummary.find(
                            (w) =>
                              w.weekNumber ===
                              weeksData.indexOf(selectedWeek) + 1,
                          )?.weekNumber
                        }
                        :
                      </strong>
                      <p className="mb-0 mt-1">
                        {moment(selectedWeek.week_start).format("MMMM DD")} -{" "}
                        {moment(selectedWeek.week_end).format("MMMM DD, YYYY")}
                      </p>
                    </div>
                  </div>
                  <Form.Select
                    size="sm"
                    style={{ width: "auto" }}
                    value={weeksData.indexOf(selectedWeek)}
                    onChange={(e) =>
                      setSelectedWeek(weeksData[parseInt(e.target.value)])
                    }
                  >
                    {weeklySummary.map((w, idx) => (
                      <option key={idx} value={idx}>
                        Week {w.weekNumber}:{" "}
                        {moment(weeksData[idx].week_start).format("MMM DD")} -{" "}
                        {moment(weeksData[idx].week_end).format("MMM DD")}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </Alert>

              <Row>
                <Col lg={12}>
                  <Card className="shadow-sm mb-4">
                    <Card.Header
                      className="bg-white"
                      style={{
                        fontWeight: "600",
                        borderBottom: "2px solid #e9ecef",
                      }}
                    >
                      <i className="fas fa-chart-line me-2 text-primary" aria-hidden="true"></i>
                      Daily Evolution - Week{" "}
                      {
                        weeklySummary.find(
                          (w) =>
                            w.weekNumber ===
                            weeksData.indexOf(selectedWeek) + 1,
                        )?.weekNumber
                      }
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart
                          data={selectedWeek.daily_calculations}
                          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={(date) =>
                              moment(date).format("ddd DD")
                            }
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis
                            yAxisId="left"
                            label={{
                              value: "mm",
                              angle: -90,
                              position: "insideLeft",
                            }}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={[0, 1.2]}
                            label={{
                              value: "Kc",
                              angle: 90,
                              position: "insideRight",
                            }}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <ReferenceLine
                            yAxisId="left"
                            y={75}
                            stroke="#ff6b6b"
                            strokeDasharray="5 5"
                            label="Stress"
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey={(day) => day.bilan?.[23]?.value || 0}
                            name={t("water_balance")}
                            stroke="#8884d8"
                            strokeWidth={3}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="kc"
                            name="Kc"
                            stroke="#ff7300"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                          />
                          <Bar
                            yAxisId="left"
                            dataKey="Etc"
                            name="ETc"
                            fill="#82ca9d"
                          />
                          <Bar
                            yAxisId="left"
                            dataKey="irrigation"
                            name={t("irrigation")}
                            fill="#0088fe"
                          />
                          <Bar
                            yAxisId="left"
                            dataKey="rain"
                            name={t("Rain")}
                            fill="#00C49F"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={12}>
                  <Card className="shadow-sm">
                    <Card.Header
                      className="bg-white"
                      style={{
                        fontWeight: "600",
                        borderBottom: "2px solid #e9ecef",
                      }}
                    >
                      <i className="fas fa-table me-2 text-secondary" aria-hidden="true"></i>
                      {t("daily_evolution_week")}
                    </Card.Header>
                    <Card.Body>
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">{t("date")}</th>
                              <th scope="col" className="text-center">{t("kc")}</th>
                              <th scope="col" className="text-center">ET0</th>
                              <th scope="col" className="text-center">ETC</th>
                              <th scope="col" className="text-center">{t("rainfall")}</th>
                              <th scope="col" className="text-center">{t("irrigation")}</th>
                              <th scope="col" className="text-center">{t("water_balance")}</th>
                              <th scope="col" className="text-center">{t("status")}</th>
                              <th scope="col" className="text-center">{t("actions")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedWeek.daily_calculations?.map(
                              (day, idx) => {
                                const balance = day.bilan?.[23]?.value || 0;
                                const status =
                                  balance >= ruMax
                                    ? "Full"
                                    : balance > ruMin
                                      ? "Optimal"
                                      : "Critical";
                                const statusColor =
                                  balance >= ruMax
                                    ? "success"
                                    : balance > ruMin
                                      ? "info"
                                      : "danger";

                                return (
                                  <tr key={idx}>
                                    <td>
                                      <strong>
                                        {moment(day.date).format("ddd, MMM DD")}
                                      </strong>
                                    </td>
                                    <td className="text-center">
                                      <Badge bg="warning" text="dark">
                                        {safeNumber(day.kc, 3)}
                                      </Badge>
                                    </td>
                                    <td className="text-center">
                                      {safeNumber(day.ET0)}
                                    </td>
                                    <td className="text-center">
                                      <Badge bg="success">
                                        {safeNumber(day.Etc)}
                                      </Badge>
                                    </td>
                                    <td className="text-center">
                                      {day.rain > 0 ? (
                                        <Badge bg="info">
                                          {safeNumber(day.rain)}
                                        </Badge>
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {day.irrigation > 0 ? (
                                        <Badge bg="primary">
                                          {safeNumber(day.irrigation)}
                                        </Badge>
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                    <td className="text-center">
                                      <Badge bg={statusColor}>
                                        {safeNumber(balance)}
                                      </Badge>
                                    </td>
                                    <td className="text-center">
                                      <Badge bg={statusColor}>{status}</Badge>
                                    </td>
                                    <td className="text-center">
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => {
                                          setSelectedDay(day);
                                          setActiveTab("hourly");
                                        }}
                                      >
                                        <i className="fas fa-clock me-1" aria-hidden="true"></i>
                                        24H
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              },
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Card className="shadow-sm">
              <Card.Body className="text-center py-5">
                <i className="fas fa-calendar-day fa-4x mb-4 text-muted opacity-25" aria-hidden="true"></i>
                <h5 className="text-muted">No Week Selected</h5>
                <p className="text-muted mb-4">
                  Select a week from the "Weekly Summary" tab to view detailed
                  daily analysis
                </p>
                <Button
                  variant="primary"
                  onClick={() => setActiveTab("weekly")}
                >
                  <i className="fas fa-calendar-week me-2" aria-hidden="true"></i>
                  Go to Weekly Summary
                </Button>
              </Card.Body>
            </Card>
          )}
        </Tab>

        {/* Hourly Analysis Tab */}
        <Tab
          eventKey="hourly"
          title={
            <>
              <i className="fas fa-clock me-2" aria-hidden="true"></i>
              {t("hourly_detail")}
            </>
          }
        >
          {selectedDay && hourlyData.length > 0 ? (
            <>
              <Alert variant="primary" className="mb-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-calendar-day fa-2x me-3" aria-hidden="true"></i>
                    <div>
                      <strong>
                        {t('water_balance_24h_evolution')}{" "}
                        {moment(selectedDay.date).format("dddd, MMMM DD, YYYY")}
                      </strong>
                      <p className="mb-0 mt-1">
                        {t('total_etc')}:{" "}
                        <strong>{safeNumber(selectedDay.Etc)} mm</strong> •
                        {t('rain_mm')}: <strong>{safeNumber(selectedDay.rain)} mm</strong>{" "}
                        • {t('irrigation_mm')}:{" "}
                        <strong>{safeNumber(selectedDay.irrigation)} mm</strong>{" "}
                        • {t('final_balance_mm')}:{" "}
                        <strong>
                          {safeNumber(selectedDay.bilan?.[23]?.value || 0)} mm
                        </strong>
                      </p>
                    </div>
                  </div>
                  {selectedWeek && (
                    <ButtonGroup>
                      {selectedWeek.daily_calculations?.map((day, idx) => (
                        <Button
                          key={idx}
                          size="sm"
                          variant={
                            selectedDay === day ? "primary" : "outline-primary"
                          }
                          onClick={() => setSelectedDay(day)}
                          title={moment(day.date).format("MMM DD")}
                        >
                          {moment(day.date).format("DD")}
                        </Button>
                      ))}
                    </ButtonGroup>
                  )}
                </div>
              </Alert>

              <Row>
                <Col lg={12}>
                  <Card className="shadow-sm mb-4">
                    <Card.Header
                      className="bg-white"
                      style={{
                        fontWeight: "600",
                        borderBottom: "2px solid #e9ecef",
                      }}
                    >
                      <i className="fas fa-chart-line me-2 text-primary" aria-hidden="true"></i>
                      {t("water_balance_24h_evolution")}
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart
                          data={hourlyData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="hour" />
                          <YAxis />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <ReferenceLine
                            y={75}
                            stroke="#ff6b6b"
                            strokeDasharray="5 5"
                            label="Stress Threshold"
                          />
                          <ReferenceLine
                            y={80}
                            stroke="#4caf50"
                            strokeDasharray="5 5"
                            label="Optimal"
                          />
                          <Line
                            type="monotone"
                            dataKey="bilan"
                            name={t("water_balance_mm")}
                            stroke="#8884d8"
                            strokeWidth={3}
                            dot={{ fill: "#8884d8", r: 3 }}
                          />
                          <Bar
                            dataKey="etc"
                            name={t("hourly_etc_mm")}
                            fill="#82ca9d"
                            opacity={0.6}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={6}>
                  <Card className="shadow-sm mb-4">
                    <Card.Header
                      className="bg-white"
                      style={{
                        fontWeight: "600",
                        borderBottom: "2px solid #e9ecef",
                      }}
                    >
                      <i className="fas fa-cloud-sun me-2 text-warning" aria-hidden="true"></i>
                      {t("etc_distribution_by_period")}
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={hourlyData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="hour" />
                          <YAxis />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar dataKey="etc" name="Hourly ETc" fill="#82ca9d">
                            {hourlyData.map((entry, index) => (
                              <Bar
                                key={`bar-${index}`}
                                fill={
                                  entry.hourNumber >= 6 &&
                                  entry.hourNumber <= 18
                                    ? "#ffa726"
                                    : "#42a5f5"
                                }
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={6}>
                  <Card className="shadow-sm mb-4">
                    <Card.Header
                      className="bg-white"
                      style={{
                        fontWeight: "600",
                        borderBottom: "2px solid #e9ecef",
                      }}
                    >
                      <i className="fas fa-chart-area me-2 text-info" aria-hidden="true"></i>
                      {t("balance_trend_through_day")}
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart
                          data={hourlyData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="hour" />
                          <YAxis domain={[60, 100]} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <ReferenceLine
                            y={75}
                            stroke="#ff6b6b"
                            strokeDasharray="3 3"
                          />
                          <ReferenceLine
                            y={80}
                            stroke="#4caf50"
                            strokeDasharray="3 3"
                          />
                          <Area
                            type="monotone"
                            dataKey="bilan"
                            name={t("balance")}
                            fill="#8884d8"
                            stroke="#8884d8"
                            fillOpacity={0.6}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={12}>
                  <Card className="shadow-sm">
                    <Card.Header
                      className="bg-white"
                      style={{
                        fontWeight: "600",
                        borderBottom: "2px solid #e9ecef",
                      }}
                    >
                      <i className="fas fa-table me-2 text-secondary" aria-hidden="true"></i>
                      {t("hourly_data_table")}
                    </Card.Header>
                    <Card.Body>
                      <div className="table-responsive">
                        <Table hover size="sm" className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">{t("hour")}</th>
                              <th scope="col" className="text-center">
                                {t("water_balance_mm")}
                              </th>
                              <th scope="col" className="text-center">
                                {t("hourly_etc_mm")}
                              </th>
                              <th scope="col" className="text-center">
                                {t("period")}
                              </th>
                              <th scope="col" className="text-center">
                                {t("status")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {hourlyData.map((hour, idx) => (
                              <tr
                                key={idx}
                                style={{
                                  backgroundColor:
                                    hour.bilan < 75 ? "#fff3cd" : "transparent",
                                }}
                              >
                                <td>
                                  <strong>{hour.hour}</strong>
                                </td>
                                <td className="text-center">
                                  <Badge
                                    bg={
                                      hour.bilan >= ruMax
                                        ? "success"
                                        : hour.bilan > ruMin
                                          ? "info"
                                          : "danger"
                                    }
                                  >
                                    {hour.bilan}
                                  </Badge>
                                </td>
                                <td className="text-center">{hour.etc}</td>
                                <td className="text-center">
                                  <Badge
                                    bg={
                                      hour.hourNumber >= 6 &&
                                      hour.hourNumber <= 18
                                        ? "warning"
                                        : "secondary"
                                    }
                                    text={
                                      hour.hourNumber >= 6 &&
                                      hour.hourNumber <= 18
                                        ? "dark"
                                        : "white"
                                    }
                                  >
                                    {t(hour.periodKey)}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <small
                                    className={`text-${
                                      hour.bilan > 80
                                        ? "success"
                                        : hour.bilan > 75
                                          ? "info"
                                          : hour.bilan > 60
                                            ? "warning"
                                            : "danger"
                                    }`}
                                  >
                                    {hour.bilan >= ruMax
                                      ? "Full"
                                      : hour.bilan > ruMin
                                        ? "Optimal"
                                        : "Critical"}
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Card className="shadow-sm">
              <Card.Body className="text-center py-5">
                <i className="fas fa-clock fa-4x mb-4 text-muted opacity-25" aria-hidden="true"></i>
                <h5 className="text-muted">No Day Selected</h5>
                <p className="text-muted mb-4">
                  Select a specific day from the "Daily Detail" tab to view
                  24-hour hourly analysis
                </p>
                <Button
                  variant="primary"
                  onClick={() => setActiveTab("daily-detail")}
                >
                  <i className="fas fa-calendar-day me-2" aria-hidden="true"></i>
                  Go to Daily Detail
                </Button>
              </Card.Body>
            </Card>
          )}
        </Tab>
      </Tabs>
    </Container>
  );
};

export default CalculationDetails;
